export const PUBLIC_ERROR_MESSAGES = {
	AUTH_REQUIRED: "Please sign in and try again.",
	AUTH_UNAUTHORIZED: "You don't have access to do that.",
	AUTH_FORBIDDEN: "You don't have access to do that.",
	ACCOUNT_DELETED: "This account is no longer available.",
	PROFILE_INCOMPLETE: "Finish setting up your profile before continuing.",
	RESOURCE_NOT_FOUND: "That item is no longer available.",
	REFERRAL_INVALID: "That referral code isn't valid.",
	VERIFICATION_INVALID: "That verification link or code is no longer valid.",
	CONFLICT: "That action can't be completed right now.",
	REGISTRATION_IN_PROGRESS:
		"We already sent a code to this phone number. Enter it or wait a moment before trying again.",
	EMAIL_ALREADY_IN_USE:
		"That email is already in use. Try signing in or use a different one.",
	INVALID_INPUT: "Check your details and try again.",
	LIMIT_REACHED: "You've reached the limit for that action.",
	KYC_REQUIRED: "Complete identity verification to continue.",
	KYC_REJECTED: "Your verification needs attention before you can continue.",
	INSUFFICIENT_BALANCE: "You don't have enough balance for that.",
	PAYMENT_STATE_INVALID: "This payment can't be completed right now.",
	CHAT_UNAVAILABLE: "Chat isn't available for this errand yet.",
	DELIVERY_INVALID: "That delivery confirmation didn't work.",
	DELIVERY_STATE_INVALID: "Delivery can't be confirmed right now.",
	DELIVERY_LOCKED: "Delivery confirmation is locked. Please contact support.",
	DELIVERY_EXPIRED: "That delivery code has expired. Please contact support.",
	OWN_ERRAND: "You can't accept your own errand.",
	OPERATION_NOT_ALLOWED: "That action isn't available right now.",
	ACTIVE_ERRAND_EXISTS:
		"You already have an active errand. Complete or cancel it first.",
	BANK_DETAILS_REQUIRED: "Add your bank details before requesting a payout.",
	BANK_DETAILS_ALREADY_SET:
		"Bank details are already on file. Contact support to make changes.",
	SIGNUP_REQUIRED: "Create an account to continue. Your progress is saved.",
	DISPUTE_REQUIRED: "This issue now needs to be resolved through a dispute.",
	UNEXPECTED_ERROR: "Something went wrong. Please try again.",
} as const;

export type PublicErrorCode = keyof typeof PUBLIC_ERROR_MESSAGES;

export type PublicErrorTraceValue =
	| null
	| string
	| number
	| boolean
	| PublicErrorTraceValue[]
	| { [key: string]: PublicErrorTraceValue | undefined };

export type PublicErrorTraceContext = Record<
	string,
	PublicErrorTraceValue | undefined
>;

export type PublicErrorTracePayload = {
	event: string;
	timestamp: string;
	functionKind: string;
	visibility: string;
	registrationSite: string | null;
	trace: Record<string, PublicErrorTraceValue | undefined>;
	identity: PublicErrorTraceValue;
	input: PublicErrorTraceValue;
	error: Record<string, PublicErrorTraceValue | undefined>;
};

export type PublicErrorData = {
	code: PublicErrorCode;
	message: string;
	traceContext?: PublicErrorTraceContext;
	trace?: PublicErrorTracePayload;
};

export type PublicErrorObservability = PublicErrorTracePayload;

const PUBLIC_ERROR_CODES = new Set<PublicErrorCode>(
	Object.keys(PUBLIC_ERROR_MESSAGES) as PublicErrorCode[],
);

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null;

export const isPublicErrorCode = (value: unknown): value is PublicErrorCode =>
	typeof value === "string" && PUBLIC_ERROR_CODES.has(value as PublicErrorCode);

export const getPublicErrorMessage = (code: PublicErrorCode) =>
	PUBLIC_ERROR_MESSAGES[code];

export const getPublicErrorMessageByCode = (code: unknown) =>
	isPublicErrorCode(code)
		? getPublicErrorMessage(code)
		: getPublicErrorMessage("UNEXPECTED_ERROR");

export const toPublicErrorData = (
	code: PublicErrorCode,
	traceContext?: PublicErrorTraceContext,
): PublicErrorData => ({
	code,
	message: getPublicErrorMessage(code),
	traceContext,
});

export const extractPublicErrorCode = (
	error: unknown,
): PublicErrorCode | null => {
	if (!isRecord(error)) {
		return null;
	}

	const data = error.data;
	if (!isRecord(data)) {
		return null;
	}

	return isPublicErrorCode(data.code) ? data.code : null;
};

export const getPublicErrorMessageForError = (error: unknown) =>
	getPublicErrorMessageByCode(extractPublicErrorCode(error));
